/** @format */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import {
    Address,
    DegreeProgramme,
    DegreeProgrammeAttainmentApplication,
    DegreeProgrammeAttainmentApplicationQuestion,
    DegreeProgrammeAttainmentApplicationQuestionnaire,
    GenericAddress,
    Module,
    Plan,
    StudyRight,
} from 'common-typescript/types';
import { Observable, take, tap } from 'rxjs';
import { LocaleService } from 'sis-common';
import {
    AlertsService,
    AppErrorHandler,
    DegreeProgrammeAttainmentApplicationQuestionnaireService,
    editContactInformationDialogOpener,
    EducationEntityService,
    FudisFormValidatorsService,
    isFinnishAddress,
    maxLength,
    SisFormBuilder,
    UniversityService,
} from 'sis-components';
import { StudentProfile, StudentProfileService } from '../../../student-profile.service';

@Component({
    selector: 'app-graduation-wizard-step3',
    templateUrl: './graduation-wizard-step3.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GraduationWizardStep3Component implements OnInit {
    @Input() degreeProgramme: Module;
    @Input() profile: StudentProfile;
    @Input() plan: Plan;
    @Input() studyRight: StudyRight;
    @Input() stepNumber: number;
    @Output() forward = new EventEmitter<Partial<DegreeProgrammeAttainmentApplication>>();

    readonly isFinnishAddress = isFinnishAddress;

    additionalInfoControl = new FormControl<string>(null);
    degreeDeliveryMethod = new FormControl<string>('PICK_UP');
    deliveryAddressType = new FormControl<string>('PRIMARY');
    joinsAlumniAssociation = new FormControl<boolean>(false);
    digitalDegreeCertificatePreferred = false;
    localProfile: StudentProfile;
    questions: DegreeProgrammeAttainmentApplicationQuestion[] = [];
    questionsForm = new FormGroup<Record<string, FormControl>>({});
    showAlumniSection = false;
    alumniAssociationDescription?: string;
    alumniAssociationInfoText?: string;
    alumniAssociationUrl?: string;
    private defaultAlumniAssociationInfoText$: Observable<string>;
    private educationHasSecondPhase = false;

    private readonly openEditContactInfoDialog = editContactInformationDialogOpener();

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly fb = inject(SisFormBuilder);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly questionnaireService = inject(DegreeProgrammeAttainmentApplicationQuestionnaireService);
    private readonly localeService: LocaleService = inject(LocaleService);
    private readonly fudisValidators = inject(FudisFormValidatorsService);
    private readonly alertsService = inject(AlertsService);
    private readonly studentProfileService = inject(StudentProfileService);
    private readonly universityService = inject(UniversityService);
    private readonly translocoService = inject(TranslocoService);
    private readonly educationEntityService = inject(EducationEntityService);

    ngOnInit(): void {
        this.localProfile = this.profile;
        if (this.degreeProgramme?.id) {
            this.loadQuestionnaire(this.degreeProgramme.id);
        }
        this.loadEducationPhaseInfo();
        if (this.plan?.graduationSurveyOpenedOn && !this.profile.graduationSurveyExemptionGranted) {
            this.studentProfileService
                .grantGraduationSurveyExemption(this.plan.id)
                .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                .subscribe();
        }
        this.universityService
            .getCurrentUniversitySettings()
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe(settings => {
                this.alumniAssociationDescription = this.localeService.localize(settings?.alumniAssociationDescription);
                this.alumniAssociationInfoText = this.localeService.localize(settings?.alumniAssociationInfoText);
                this.alumniAssociationUrl = this.localeService.localize(settings?.alumniAssociationUrl);
                this.digitalDegreeCertificatePreferred = !!settings?.frontendFeatureToggles?.digitalDegreeCertificatePreferred;
                if (!this.digitalDegreeCertificatePreferred) {
                    this.degreeDeliveryMethod.addValidators(this.fudisValidators.required());
                }
                this.updateAlumniSectionVisibility();
                this.cdr.markForCheck();
            });
    }

    private updateAlumniSectionVisibility(): void {
        this.showAlumniSection = !!this.alumniAssociationUrl && this.isLastPhaseOfEducation();
    }

    private isLastPhaseOfEducation(): boolean {
        // Single-phase education: graduating it is always the final phase.
        if (!this.educationHasSecondPhase) {
            return true;
        }
        // Two-phase (bachelor's + master's) education: this is the final phase only when the student is
        // graduating the confirmed phase 2 programme. Otherwise they are still graduating the bachelor's.
        const phase2GroupId = this.studyRight?.acceptedSelectionPath?.educationPhase2GroupId;
        return !!phase2GroupId && (this.degreeProgramme as DegreeProgramme)?.groupId === phase2GroupId;
    }

    private loadEducationPhaseInfo(): void {
        const educationId = this.studyRight?.educationId;
        if (!educationId) {
            this.educationHasSecondPhase = false;
            this.updateAlumniSectionVisibility();
            this.cdr.markForCheck();
            return;
        }
        this.educationEntityService
            .getById(educationId)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe(education => {
                this.educationHasSecondPhase = !!education?.structure?.phase2;
                this.updateAlumniSectionVisibility();
                this.cdr.markForCheck();
            });
    }

    getDefaultAlumniAssociationInfoText(): Observable<string> {
        if (!this.defaultAlumniAssociationInfoText$) {
            this.defaultAlumniAssociationInfoText$ = this.translocoService.selectTranslate(
                'STUDENT_APPLICATIONS.DEGREE_PROGRAMME_ATTAINMENT_APPLICATION.ALUMNI_ASSOCIATION.INFO_TEXT_PLACEHOLDER',
                null,
                this.localeService.getCurrentLanguage(),
            );
        }
        return this.defaultAlumniAssociationInfoText$;
    }

    get degreeProgrammeName(): string {
        return this.localeService.localize(this.degreeProgramme?.name);
    }

    parsedGenericAddress(address: Address): string {
        return (address as GenericAddress).address?.replace(/\r?\n/g, ', ').trim() ?? '';
    }

    submit(): void {
        this.questionsForm.markAllAsTouched();
        this.degreeDeliveryMethod.markAsTouched();

        if (this.questionsForm.invalid || this.degreeDeliveryMethod.invalid) {
            this.alertsService.addFormSubmissionFailureAlert();
            return;
        }

        const answers = this.questions.map(question => ({
            answer: this.questionsForm.get(question.localId)?.value ?? null,
            guidance: question.guidance,
            question: question.question,
            questionId: question.localId,
            required: question.required,
        }));

        const deliveryAddress = this.resolveDeliveryAddress();

        this.forward.emit({
            degreeDeliveryMethod: this.digitalDegreeCertificatePreferred
                ? 'DIGITAL_CERTIFICATE'
                : (this.degreeDeliveryMethod.value as DegreeProgrammeAttainmentApplication['degreeDeliveryMethod']),
            deliveryAddress,
            additionalInfo: this.additionalInfoControl.value,
            questionnaireAnswers: answers.length > 0 ? answers : undefined,
            joinsAlumniAssociation: this.showAlumniSection ? (this.joinsAlumniAssociation.value ?? false) : undefined,
        });
    }

    /**
     * Only mailed certificates need a delivery address. For pick-up and digital certificates the address is
     * irrelevant, so we leave it null instead of defaulting to the student's primary address (which would
     * otherwise be shown unnecessarily in the staff processing view). Digital certificate preference overrides
     * the delivery method on emit, so it must short-circuit here too to keep the address consistent.
     */
    private resolveDeliveryAddress(): Address | null {
        if (this.digitalDegreeCertificatePreferred || this.degreeDeliveryMethod.value !== 'MAIL') {
            return null;
        }
        if (this.deliveryAddressType.value === 'SECONDARY') {
            return this.localProfile.secondaryAddress ?? this.localProfile.primaryAddress;
        }
        return this.localProfile.primaryAddress;
    }

    getQuestionControl(key: string): FormControl {
        return this.questionsForm.get(key) as FormControl;
    }

    openEditContactInformation(): void {
        this.openEditContactInfoDialog({ studentProfile: this.localProfile })
            .afterClosed()
            .subscribe(result => {
                if (result?.studentProfile) {
                    this.localProfile = result.studentProfile as StudentProfile;
                    this.cdr.markForCheck();
                }
            });
    }

    private loadQuestionnaire(degreeProgrammeId: string): void {
        this.questions = [];
        this.questionsForm = new FormGroup<Record<string, FormControl>>({});
        this.questionnaireService
            .getByDegreeProgrammeId(degreeProgrammeId)
            .pipe(
                take(1),
                tap((questionnaire: DegreeProgrammeAttainmentApplicationQuestionnaire | null): void => {
                    if (questionnaire) {
                        this.questions = questionnaire.questions.filter(question => question.state === 'ACTIVE');
                        this.buildQuestionOnForm();
                    }
                    this.cdr.markForCheck();
                }),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();
    }

    private buildQuestionOnForm(): void {
        this.questions.forEach((question: DegreeProgrammeAttainmentApplicationQuestion) => {
            this.questionsForm.addControl(
                question.localId,
                this.fb.control(null, {
                    validators: question.required ? [this.fudisValidators.required(), maxLength(1024)] : [maxLength(1024)],
                }),
            );
        });
    }
}
